import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Download, Check, Smartphone, Apple, Chrome, Share } from "lucide-react";
import { useNavigate } from "react-router-dom";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

const Install = () => {
  const navigate = useNavigate();
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isAndroid, setIsAndroid] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    // Detect platform
    const userAgent = navigator.userAgent.toLowerCase();
    setIsIOS(/iphone|ipad|ipod/.test(userAgent));
    setIsAndroid(/android/.test(userAgent));
    
    // Check if already in standalone mode
    setIsStandalone(window.matchMedia("(display-mode: standalone)").matches);

    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    };

    window.addEventListener("beforeinstallprompt", handler);
    window.addEventListener("appinstalled", () => setIsInstalled(true));

    return () => {
      window.removeEventListener("beforeinstallprompt", handler);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    await deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === "accepted") {
      setIsInstalled(true);
    }
    setDeferredPrompt(null);
  };

  if (isStandalone || isInstalled) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-b from-background to-muted/20">
      <Card className="max-w-md w-full text-center">
        <CardHeader>
          <div className="mx-auto mb-4 p-4 rounded-full bg-primary/10">
            <Check className="h-12 w-12 text-primary" />
          </div>
          <CardTitle className="text-2xl">Strata is Installed!</CardTitle>
            <CardDescription>
              You can now access Strata from your home screen.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => navigate("/dashboard")} className="w-full">
              Go to Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-b from-background to-muted/20">
      <Card className="max-w-md w-full">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4">
            <img src="/logo.png" alt="Strata" className="h-16 w-16 rounded-2xl" />
          </div>
          <CardTitle className="text-2xl">Install Strata</CardTitle>
          <CardDescription>
            Get faster access, offline support, and a native app experience.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Benefits */}
          <div className="grid gap-3">
            {[
              { icon: "⚡", text: "Instant loading & offline access" },
              { icon: "🔔", text: "Push notifications for updates" },
              { icon: "📱", text: "Full-screen native experience" },
              { icon: "🚀", text: "Quick launch from home screen" },
            ].map((benefit, i) => (
              <div key={i} className="flex items-center gap-3 text-sm">
                <span className="text-xl">{benefit.icon}</span>
                <span className="text-muted-foreground">{benefit.text}</span>
              </div>
            ))}
          </div>

          <div className="border-t pt-4">
            {/* Chrome/Android Install Button */}
            {deferredPrompt && (
              <Button onClick={handleInstall} className="w-full gap-2" size="lg">
                <Download className="h-5 w-5" />
                Install Strata
              </Button>
            )}

            {/* iOS Instructions */}
            {isIOS && !deferredPrompt && (
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-sm font-medium">
                  <Apple className="h-4 w-4" />
                  Install on iPhone/iPad
                </div>
                <ol className="text-sm text-muted-foreground space-y-2">
                  <li className="flex items-start gap-2">
                    <span className="font-medium text-foreground">1.</span>
                    <span>Tap the <Share className="inline h-4 w-4 mx-1" /> Share button in Safari</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="font-medium text-foreground">2.</span>
                    <span>Scroll down and tap "Add to Home Screen"</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="font-medium text-foreground">3.</span>
                    <span>Tap "Add" to install</span>
                  </li>
                </ol>
              </div>
            )}

            {/* Android/Chrome fallback */}
            {isAndroid && !deferredPrompt && (
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-sm font-medium">
                  <Chrome className="h-4 w-4" />
                  Install on Android
                </div>
                <ol className="text-sm text-muted-foreground space-y-2">
                  <li className="flex items-start gap-2">
                    <span className="font-medium text-foreground">1.</span>
                    <span>Tap the menu (⋮) in Chrome</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="font-medium text-foreground">2.</span>
                    <span>Tap "Add to Home screen" or "Install app"</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="font-medium text-foreground">3.</span>
                    <span>Tap "Install" to confirm</span>
                  </li>
                </ol>
              </div>
            )}

            {/* Desktop fallback */}
            {!isIOS && !isAndroid && !deferredPrompt && (
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-sm font-medium">
                  <Smartphone className="h-4 w-4" />
                  Install on Desktop
                </div>
                <p className="text-sm text-muted-foreground">
                  Look for the install icon in your browser's address bar, or open this page on your mobile device.
                </p>
              </div>
            )}
          </div>

          <Button 
            variant="ghost" 
            className="w-full" 
            onClick={() => navigate("/")}
          >
            Continue in Browser
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default Install;
